import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wind, LogOut, RefreshCw, ExternalLink, Loader2, Inbox,
  MessageCircle, Mail, MapPin, Building2, Phone,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { business } from "@/config/business";

const STATUS_META = {
  lead: { label: "Lead", color: "bg-slate-100 text-slate-700 border-slate-200" },
  qualified: { label: "Qualified", color: "bg-blue-50 text-blue-700 border-blue-200" },
  survey: { label: "Survey", color: "bg-amber-50 text-amber-700 border-amber-200" },
  quotation: { label: "Quotation", color: "bg-purple-50 text-purple-700 border-purple-200" },
  contract: { label: "Contract", color: "bg-[#25D366]/10 text-[#1a9e4b] border-[#25D366]/30" },
};
const STATUS_ORDER = ["lead", "qualified", "survey", "quotation", "contract"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/admin/leads", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/admin/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/admin/leads/${id}/status`, { status });
      const statsRes = await api.get("/admin/stats");
      setStats(statsRes.data);
      if (filter !== "all" && filter !== status) {
        setLeads((prev) => prev.filter((l) => l.id !== id));
      } else {
        setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      }
    } catch {
      /* ignore */
    } finally {
      setUpdating(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-[#0F172A]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#25D366] text-white">
              <Wind className="h-5 w-5" />
            </span>
            <div>
              <h1 className="text-sm font-extrabold text-white">Dashboard Lead</h1>
              <p className="text-[11px] text-slate-400">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              data-testid="admin-view-site"
              className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/10 hover:text-white sm:inline-flex"
            >
              <ExternalLink className="h-4 w-4" /> Lihat Website
            </a>
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              aria-label="Muat ulang"
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20"
            >
              <LogOut className="h-4 w-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="stat-total">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total</p>
            <p className="mt-1 text-2xl font-extrabold text-slate-900">{stats.total}</p>
          </div>
          {STATUS_ORDER.map((s) => (
            <div key={s} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`stat-${s}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{STATUS_META[s].label}</p>
              <p className="mt-1 text-2xl font-extrabold text-slate-900">{stats.by_status?.[s] ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="mt-6 flex flex-wrap gap-2">
          {["all", ...STATUS_ORDER].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                filter === s
                  ? "bg-[#0F172A] text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
              }`}
            >
              {s === "all" ? "Semua" : STATUS_META[s].label}
            </button>
          ))}
        </div>

        {/* Leads */}
        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-24">
              <Loader2 className="h-7 w-7 animate-spin text-[#25D366]" />
            </div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white py-24 text-center" data-testid="admin-empty">
              <Inbox className="h-10 w-10 text-slate-300" />
              <p className="mt-4 font-bold text-slate-700">Belum ada lead</p>
              <p className="mt-1 text-sm text-slate-500">Lead dari formulir website akan muncul di sini.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-leads-list">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  data-testid={`admin-lead-${lead.id}`}
                  className="rounded-xl border border-slate-200 bg-white p-5"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-bold text-slate-900">{lead.nama}</h3>
                        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${STATUS_META[lead.status]?.color || ""}`}>
                          {STATUS_META[lead.status]?.label || lead.status}
                        </span>
                        {lead.audience && (
                          <span className="rounded-full border border-[#E0A400]/40 bg-[#E0A400]/10 px-2.5 py-0.5 text-xs font-bold text-[#a97e08]">
                            {lead.audience}
                          </span>
                        )}
                        <span className="text-xs text-slate-400">{fmtDate(lead.created_at)}</span>
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm text-slate-600 sm:grid-cols-2 lg:grid-cols-3">
                        {lead.perusahaan && (
                          <span className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" />{lead.perusahaan}{lead.jabatan ? ` · ${lead.jabatan}` : ""}</span>
                        )}
                        <a href={`https://wa.me/${lead.whatsapp.replace(/[^0-9]/g, "").replace(/^0/, "62")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#25D366]"><MessageCircle className="h-4 w-4 text-slate-400" />{lead.whatsapp}</a>
                        {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-2 hover:text-slate-900"><Mail className="h-4 w-4 text-slate-400" />{lead.email}</a>}
                        {lead.lokasi && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{lead.lokasi}</span>}
                        <span className="flex items-center gap-2"><Inbox className="h-4 w-4 text-slate-400" />{lead.jenis_bisnis} · {lead.kebutuhan}</span>
                        {lead.jumlah_unit && <span className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" />{lead.jumlah_unit} unit{lead.jenis_ac ? ` · ${lead.jenis_ac}` : ""}</span>}
                        {lead.sistem_hvac && <span className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" />HVAC: {lead.sistem_hvac}</span>}
                        {lead.estimasi_scope && <span className="flex items-center gap-2"><Inbox className="h-4 w-4 text-slate-400" />Scope: {lead.estimasi_scope}</span>}
                      </div>

                      {lead.keterangan && (
                        <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{lead.keterangan}</p>
                      )}
                    </div>

                    <div className="flex flex-none items-center gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Status</label>
                      <div className="relative">
                        <select
                          value={lead.status}
                          onChange={(e) => handleStatus(lead.id, e.target.value)}
                          disabled={updating === lead.id}
                          data-testid={`status-select-${lead.id}`}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#25D366] disabled:opacity-60"
                        >
                          {STATUS_ORDER.map((s) => (
                            <option key={s} value={s}>{STATUS_META[s].label}</option>
                          ))}
                        </select>
                        {updating === lead.id && (
                          <Loader2 className="absolute -right-6 top-2.5 h-4 w-4 animate-spin text-[#25D366]" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
